import { useAutoAnimate } from "@formkit/auto-animate/react";
import { DownArrowIcon } from "@xola/icons";
import { Checkbox } from "@xola/ui-kit";
import { isPurchaseAbandoned, PurchaseItemStatus, toDayJs } from "common";
import dayjs, { Dayjs } from "dayjs";
import { sortBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Conversation, ExperiencePurchaseItem, PaymentReminderType, Permission, Purchase } from "types";
import { api } from "../../api/api";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useSelection } from "../../hooks/useSelection";
import {
    findPaymentReminder,
    isInvoicePurchase,
    isPendingPurchase,
    isPurchaseBalanceDue,
    isPurchaseRefundDue,
} from "../../models/Purchase";
import { InfiniteList } from "../Common/InfiniteList";
import { MultiSelectPopover } from "../MultiSelectPopover";
import { AbandonPurchase, AcceptOrDeclinePurchase, CollectBalance } from "./NotificationTypes";
import { UnreadMessage } from "./UnreadMessage";

interface NotificationsDrawerProps {
    purchases: Purchase[];
    abandonedPurchases: Purchase[];
    conversations: Conversation[];
    hasNextPage?: boolean;
    isLoading?: boolean;
    fetchNextPage: () => void;
}

interface PurchaseNotificationItem extends Purchase {
    fieldToSortByDate: Dayjs;
}

const abandonedButton = {
    id: "1",
    name: "Abandoned Carts",
    permissions: [Permission.ABANDONED_VIEW, Permission.PURCHASES_VIEW],
};
const balancesDueButton = { id: "2", name: "Balances Due" };
const invoicesDueButton = { id: "3", name: "Invoices Due" };
const pendingConfigButton = { id: "4", name: "Pending Confirmation" };
const messagesButton = { id: "5", name: "Unread Messages" };
const refundDueButton = { id: "6", name: "Refund Due" };

type FilterButtons = Array<{ id: string; name: string; permissions?: Permission[] }>;
const buttons: FilterButtons = [
    messagesButton,
    abandonedButton,
    balancesDueButton,
    refundDueButton,
    invoicesDueButton,
    pendingConfigButton,
];
const initialState = { all: true, selected: [] };
const buttonItems = buttons.map((button) => ({ id: button.id }));

export const NotificationsDrawer = ({
    purchases: purchasesNotify,
    abandonedPurchases,
    conversations: initialConversations,
    isLoading = false,
    hasNextPage,
    fetchNextPage,
}: NotificationsDrawerProps) => {
    const { isGranted } = useAuthorization();
    const invalidate = useInvalidate();
    const [animateRef] = useAutoAnimate();
    const queryClient = useQueryClient();
    const [conversations, setConversations] = useState<Conversation[]>(initialConversations);
    const [isVisible, setIsVisible] = useState(false);

    const sortedPurchases = useMemo(
        () => sortPurchases([...purchasesNotify, ...abandonedPurchases]),
        [abandonedPurchases, purchasesNotify],
    );
    const [purchases, setPurchases] = useState<Purchase[]>(sortedPurchases);
    const [activeFilters, setActiveFilters] = useState<string[]>([]);
    const [isAllSelected, setIsAllSelected] = useState(true);

    useEffect(() => setPurchases(sortedPurchases), [sortedPurchases]);

    const selection = useSelection(buttonItems, false, initialState);

    const { mutateAsync: removeNotifyPurchase } = useMutation(async (purchaseId: ID) => {
        return api.patch(`/purchases/${purchaseId}`, { notifyAt: null });
    });

    const { mutateAsync: removeAbandonedPurchase } = useMutation(async (abandonedPurchaseId: ID) => {
        const filteredItems = purchases
            .filter((purchase) => purchase.id === abandonedPurchaseId)
            .map((purchase) => purchase.items.map((item) => ({ id: item.id, status: "dismissed" })));
        const payload = { items: filteredItems[0] };
        return api.patch(`/abandonedPurchases/${abandonedPurchaseId}`, payload);
    });

    const handlePurchaseFiltering = (purchaseId: string) => {
        setPurchases((previous) => previous.filter((purchase) => purchase.id !== purchaseId));
        invalidate("purchases", purchaseId);
        queryClient.invalidateQueries("purchaseNotifications");
    };

    const handlePurchaseDismiss = async (purchaseId: ID) => {
        await removeNotifyPurchase(purchaseId);
        handlePurchaseFiltering(purchaseId);
    };

    const handleAbandonedPurchaseDismiss = async (purchaseId: ID) => {
        await removeAbandonedPurchase(purchaseId);
        handlePurchaseFiltering(purchaseId);
        invalidate("abandonedPurchases");
    };

    const handleConversationDismiss = async (conversationId: ID) => {
        setConversations(conversations.filter((conversation) => conversation.id !== conversationId));
        handleConversationClose();
    };

    const handleConversationClose = async () => queryClient.invalidateQueries("unreadConversations");

    const filteredPurchases = useMemo(() => {
        if (isAllSelected) return purchases;

        return purchases.filter((purchase) => {
            const [isAbandoned, isPending, hasBalance, isRefund, hasInvoice] = getPurchaseType(purchase);

            return (
                (activeFilters.includes(abandonedButton.id) && isAbandoned) ||
                (activeFilters.includes(pendingConfigButton.id) && isPending) ||
                (activeFilters.includes(invoicesDueButton.id) && hasInvoice) ||
                (activeFilters.includes(balancesDueButton.id) && hasBalance) ||
                (activeFilters.includes(refundDueButton.id) && isRefund)
            );
        });
    }, [purchases, activeFilters, isAllSelected]);

    const sortedPurchasesToDisplay = useMemo(() => getSortedPurchases(filteredPurchases), [filteredPurchases]);

    const handleFilterAction = () => {
        setActiveFilters(selection.state.selected);
        setIsAllSelected(selection.isSelectedAll);
        setIsVisible(false);
    };

    return (
        <div>
            <MultiSelectPopover
                title="Filter Notifications"
                isVisible={isVisible}
                zIndex={50}
                placement="auto"
                selectedCount={selection.state.all ? "All" : selection.state.selected.length}
                target={<PopoverTarget onToggle={() => setIsVisible(true)} />}
                showSearch={false}
                onApply={handleFilterAction}
                onCancel={() => setIsVisible(false)}
            >
                <MultiSelectPopover.Item key="selectAll" className="bg-gray-lighter">
                    <Checkbox
                        label="Select All"
                        checked={selection.isSelectedAll}
                        onChange={() => selection.selectAll()}
                    />
                </MultiSelectPopover.Item>
                {buttons
                    .filter((button) =>
                        button.permissions ? button.permissions.every((permission) => isGranted(permission)) : true,
                    )
                    .map((button) => (
                        <MultiSelectPopover.Item key={button.name}>
                            <Checkbox
                                label={button.name}
                                checked={selection.isSelected({ id: button.id })}
                                onChange={() => selection.select({ id: button.id })}
                            />
                        </MultiSelectPopover.Item>
                    ))}
            </MultiSelectPopover>

            {(isAllSelected || activeFilters.includes(messagesButton.id)) &&
                conversations.map((conversation) => {
                    return (
                        <UnreadMessage
                            key={conversation.id}
                            conversation={conversation}
                            onDismiss={handleConversationDismiss}
                            onClose={handleConversationClose}
                        />
                    );
                })}

            <InfiniteList
                isLoading={isLoading}
                hasMore={hasNextPage && (selection.isSelectedAll || selection.hasSelection)}
                onLoadMore={fetchNextPage}
            >
                <span ref={animateRef}>
                    {sortedPurchasesToDisplay.map((purchase) => {
                        const [isAbandoned, isPending, hasBalance, isRefund, hasInvoice] = getPurchaseType(purchase);
                        if ((isAllSelected || activeFilters.includes(abandonedButton.id)) && isAbandoned) {
                            return (
                                <AbandonPurchase
                                    key={purchase.id}
                                    purchase={purchase}
                                    onDismiss={handleAbandonedPurchaseDismiss}
                                />
                            );
                        }

                        if ((isAllSelected || activeFilters.includes(pendingConfigButton.id)) && isPending) {
                            return (
                                <AcceptOrDeclinePurchase
                                    key={purchase.id}
                                    purchase={purchase}
                                    onDismiss={handlePurchaseDismiss}
                                    onSuccess={handlePurchaseFiltering}
                                />
                            );
                        }

                        if ((isAllSelected || activeFilters.includes(balancesDueButton.id)) && hasBalance) {
                            return (
                                <CollectBalance
                                    key={purchase.id}
                                    purchase={purchase}
                                    onDismiss={handlePurchaseDismiss}
                                />
                            );
                        }

                        if ((isAllSelected || activeFilters.includes(invoicesDueButton.id)) && hasInvoice) {
                            return (
                                <CollectBalance
                                    key={purchase.id}
                                    purchase={purchase}
                                    onDismiss={handlePurchaseDismiss}
                                />
                            );
                        }

                        if ((isAllSelected || activeFilters.includes(refundDueButton.id)) && isRefund) {
                            return (
                                <CollectBalance
                                    key={purchase.id}
                                    purchase={purchase}
                                    onDismiss={handlePurchaseDismiss}
                                />
                            );
                        }

                        return null;
                    })}
                </span>
            </InfiniteList>
        </div>
    );
};

const PopoverTarget = ({ onToggle }: { onToggle: () => void }) => {
    return (
        <div
            className="flex w-full cursor-pointer items-center justify-between rounded border border-gray p-2 text-gray-darker"
            onClick={onToggle}
        >
            <div>Filter Notifications</div>
            <DownArrowIcon />
        </div>
    );
};

const getPurchaseType = (purchase: Purchase) => {
    const isAbandoned = isPurchaseAbandoned(purchase);
    const isPending = isPendingPurchase(purchase);
    const isRefund = isPurchaseRefundDue(purchase);
    const hasBalanceDue = isPurchaseBalanceDue(purchase);
    const hasInvoice = isInvoicePurchase(purchase);

    return [
        isAbandoned,
        !isAbandoned && isPending,
        !isAbandoned && !isPending && hasBalanceDue,
        !isAbandoned && !isPending && isRefund,
        !isAbandoned && !isPending && hasInvoice && hasBalanceDue,
    ];
};

const sortPurchases = (purchases: Purchase[]) => {
    return sortBy(purchases, (itr) => itr.notifyAt ?? itr.createdAt).reverse();
};

const getSortedPurchases = (purchases: Purchase[]): PurchaseNotificationItem[] => {
    const purchasesWithSortField = purchases
        .filter((purchase) => purchase.items)
        .map((purchase) => {
            const [isAbandoned, isPending, hasBalance, isRefund, hasInvoice] = getPurchaseType(purchase);
            let newElement: PurchaseNotificationItem = { ...purchase, fieldToSortByDate: dayjs() };

            if (isAbandoned) {
                const abandonedItem = purchase.items?.find((item) => {
                    return item.status === PurchaseItemStatus.ABANDONED;
                }) as ExperiencePurchaseItem;

                if (!abandonedItem) {
                    // If there is no abandoned item, then we don't need to display the purchase
                    // so returning undefined and filtering out in next stage
                    return undefined;
                }

                newElement = {
                    ...purchase,
                    fieldToSortByDate: abandonedItem.arrivalDateTime,
                };
            }

            if (isPending) {
                const pendingItem = purchase.items?.find((item) => {
                    return [PurchaseItemStatus.PENDING_ACCEPT, PurchaseItemStatus.PENDING_MINIMUM].includes(
                        item.status,
                    );
                }) as ExperiencePurchaseItem;

                newElement = { ...purchase, fieldToSortByDate: pendingItem.arrivalDateTime };
            }

            if (hasBalance || hasInvoice || isRefund) {
                const balanceDueTraveler = findPaymentReminder(purchase, [
                    PaymentReminderType.BALANCE_DUE_TRAVELER,
                    PaymentReminderType.SELLER_DEPOSIT,
                ]);

                const purchaseItem = purchase.items?.[0] as ExperiencePurchaseItem;
                if (balanceDueTraveler) {
                    const dueAt = toDayJs(balanceDueTraveler.dueAt);
                    newElement = { ...purchase, fieldToSortByDate: dueAt };
                } else if (purchase.items.length === 1 && purchaseItem.arrivalDate) {
                    newElement = { ...purchase, fieldToSortByDate: purchaseItem.arrivalDateTime };
                }

                if (isInvoicePurchase(purchase)) {
                    const invoiceTraveler = findPaymentReminder(purchase, [
                        PaymentReminderType.INVOICE_SELLER,
                        PaymentReminderType.INVOICE_TRAVELER,
                    ]);
                    if (invoiceTraveler?.dueAt) {
                        const dueAt = toDayJs(invoiceTraveler.dueAt);
                        newElement = { ...purchase, fieldToSortByDate: dueAt };
                    }
                }
            }

            return newElement;
        })
        .filter(Boolean) as PurchaseNotificationItem[];

    return sortBy(purchasesWithSortField, "fieldToSortByDate");
};
